import React, { useState, useEffect } from "react";
import { Box, Switch, Tooltip, Typography } from "@mui/material";
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import api from "../../Api/apiAxios";
import Loading from "../../Components/Loading";
import Mensagem from "../../Components/Mensagem";
import { ButtonBase, TextFieldBase } from "../Pedidos/styles";
import PainelCard from "./PainelCard";

const FaturamentoCard = () => {
    const nivelUsuario = window.localStorage.getItem('nivelUsuario');
    const idUsuario = window.localStorage.getItem('idUsuario');

    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState(null);
    const [enviarEmail, setEnviarEmail] = useState(false);
    const [mensagemErro, setMensagemErro] = useState('');
    const [periodo, setPeriodo] = useState("");

    useEffect(() => {
        const periodoAtual = getPeriodoAtualFormatado();
        setPeriodo(periodoAtual);
    }, []);

    const getPeriodoAtualFormatado = () => {
        const dataAtual = new Date();
        const dia = dataAtual.getDate();

        const mesReferencia = dia > 25 ? dataAtual.getMonth() + 1 : dataAtual.getMonth();
        const anoReferencia = dia > 25 && mesReferencia === 12 ? dataAtual.getFullYear() + 1 : dataAtual.getFullYear();

        const mesFormatado = String(mesReferencia + 1).padStart(2, "0");

        return `${anoReferencia}-${mesFormatado}-01`;
    }

    function handleChangePeriodo(e) {
        setPeriodo(`${e.target.value}-01`);
    }

    async function handleGerarFaturamento(ev) {
        ev.preventDefault();
        setLoading(true);

        try {
            const msgContrato = await handleGerarFaturamentoContrato();
            const msgPedidos = await handleGerarFaturamentoPedidos();
            const msgServicos = await handleGerarFaturamentoServicos();

            const mensagens = [msgContrato, msgPedidos, msgServicos].filter(Boolean);

            const periodoJaConsolidado = mensagens.every(msg =>
                msg.includes("já foi consolidado") ||
                msg.includes("Não é possível refazer o faturamento")
            );

            if (periodoJaConsolidado) {
                setMensagem(`O faturamento do período já está consolidado. Não é possível refazer o faturamento.`);
                return;
            }

            try {
                await api.post('/Faturamento/registrar-log-geracao', null, {
                    params: {
                        periodo: periodo,
                        usuarioId: idUsuario,
                        emailEnviado: enviarEmail
                    }
                });
            } catch (error) {
                console.log("Erro ao registrar log de faturamento: ", error);
            }

            setMensagem(`Faturamento gerado com sucesso!\n${mensagens.join("\n")}`);
        } catch (error) {
            console.log("Erro ao gerar faturamento: ", error);
            setMensagemErro(error.message || "Erro desconhecido.");
        } finally {
            setLoading(false);
        }
    }

    async function handleConsolidarFaturamento() {
        try {
            const response = await api.post('/Faturamento/consolidar', null, {
                params: {
                    periodo: periodo,
                    usuarioConsolidacaoId: idUsuario
                }
            });

            if (response.data?.sucesso) {
                setMensagem(response.data.mensagem || "Faturamento consolidado com sucesso!");
            }
        } catch (error) {
            console.log("Erro ao consolidar faturamento: ", error);
            setMensagemErro(error?.response?.data?.mensagem || error.message || "Erro desconhecido.");
        }

    }

    async function handleGerarFaturamentoContrato() {
        const email = enviarEmail ? 1 : 0;
        try {
            const response = await api.get(`/Faturamento/${email}/${periodo}`, {
                params: {
                    usuarioId: idUsuario
                }
            });
            return response.data;
        } catch (error) {
            console.log("Erro ao gerar faturamento contratos: ", error);
            return "Erro ao gerar faturamento de contratos.";
        }
    }

    async function handleGerarFaturamentoPedidos() {
        try {
            const response = await api.get(`/Faturamento/pedidos/${periodo}`, {
                params: {
                    usuarioId: idUsuario
                }
            });
            return response.data;
        } catch (error) {
            console.log("Erro ao gerar faturamento pedidos: ", error);
            return "Erro ao gerar faturamento de pedidos.";
        }
    }

    async function handleGerarFaturamentoServicos() {
        try {
            const response = await api.get(`/Faturamento/servicos/${periodo}`, {
                params: {
                    usuarioId: idUsuario
                }
            });
            return response.data;
        } catch (error) {
            console.log("Erro ao gerar faturamento serviços: ", error);
            return "Erro ao gerar faturamento de serviços.";
        }
    }

    return (
        <PainelCard
            icon={<ReceiptLongOutlinedIcon />}
            iconBg="rgba(25, 118, 210, 0.12)"
            iconColor="#1976d2"
            title="Geração de Faturamento"
            chipLabel="Mensal"
            chipBg="rgba(46, 125, 50, 0.12)"
            chipColor="#2e7d32"
            description="Gera o faturamento de contratos, pedidos e serviços liberados dentro do período selecionado."
        >
            {loading ? (
                <Loading />
            ) : (
                <>
                    {mensagemErro && <Mensagem mensagem={mensagemErro} setMensagem={setMensagemErro} variant="danger" />}

                    <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, fontWeight: 500, color: '#132350' }}>
                            Selecione o mês
                            <Tooltip title="Mês de referência utilizado para gerar o faturamento de contratos, pedidos e serviços.">
                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                            </Tooltip>
                        </Typography>
                        <TextFieldBase
                            id="periodo-faturamento"
                            type="month"
                            fullWidth
                            value={periodo.slice(0, 7)}
                            onChange={handleChangePeriodo}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'rgba(19, 35, 80, 0.03)',
                            borderRadius: '12px',
                            p: 2,
                            mt: 3,
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#132350', textAlign: 'left' }}>
                                Enviar e-mail para as revendas
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Notifica as revendas quando o faturamento for finalizado.
                            </Typography>
                        </Box>
                        <Switch checked={enviarEmail} onChange={() => setEnviarEmail(!enviarEmail)} />
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 3 }}>
                        <ButtonBase
                            onClick={handleGerarFaturamento}
                            variant="contained"
                            startIcon={<BoltIcon />}
                            disabled={nivelUsuario !== "1"}
                        >
                            Gerar Faturamento
                        </ButtonBase>

                        <ButtonBase
                            onClick={handleConsolidarFaturamento}
                            variant="outlined"
                            startIcon={<TaskAltIcon />}
                            disabled={nivelUsuario !== "1"}
                        >
                            Consolidar Faturamento
                        </ButtonBase>
                    </Box>

                    {mensagem && <Mensagem mensagem={mensagem} setMensagem={setMensagem} variant="warning" />}
                </>
            )}
        </PainelCard>
    );
};

export default FaturamentoCard;
